import React, { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { useMutation } from '@pv/common/hooks';
import { Fields, SidebarDrawer } from '@pv/common/components';
import { moneyPrintRound } from '@pv/common/utils';
import { sortBy } from 'lodash';

const adminVenueFields = gql`
  fragment AdminVenueFields on AdminVenue {
    name
    slug
    email
    timezone
    eventsCount
    featureFlags
    helloLeadFormPath
    featureScope {
      basicFeaturesEnabled
      proFeaturesEnabled
      premiumFeaturesEnabled
      enterpriseFeaturesEnabled
    }
    customerStartedAt
    suspicious
    achEnabled
    liveAvailability
    paymentsEnabled
    spacesCount
    createdAt
    googlePlaceInfo {
      id
      placeId
    }
    expressBookSettings {
      id
      enabled
    }
    stripeAccount {
      id
      name
      stripeAccountId
    }
    websiteUrl
    organization {
      id
      name
    }
  }
`;

export const adminUpdateVenueMutation = () => gql`
  mutation AdminUpdateVenue($input: AdminUpdateVenueInput!) {
    adminUpdateVenue(input: $input) {
      adminVenue {
        id
        ...AdminVenueFields
      }
      errors {
        message
      }
    }
  }
  ${adminVenueFields}
`;

const createVenueMutation = () => gql`
  mutation CreateVenue($input: CreateVenueInput!) {
    createVenue(input: $input) {
      venue {
        id
        venueMemberships {
          id
          user {
            id
            magicLoginLink
          }
        }
      }
      errors {
        message
      }
    }
  }
`;

export const planDefinitionsQuery = gql`
  query AdminPlanDefinitions {
    adminPlanDefinitions {
      id
      tier
      planName
      billingInterval
      monthlyRate
      achApplicationFeeRate
      cardApplicationFeeRate
      stripeProductId
      stripePriceId
      active
    }
  }
`;

export const VenueDrawer = ({
  organization,
  venue,
  open,
  onClose,
  refetchVenues,
}) => {
  const [name, setName] = useState('');
  const [numberOfTrialDays, setNumberOfTrialDays] = useState(14);
  const [selectedPlanDefinitionId, setSelectedPlanDefinitionId] = useState('');
  const [achEnabled, setAchEnabled] = useState(false);
  const [forceValidate, setForceValidate] = useState(false);

  const { data, loading: plansLoading } = useQuery(planDefinitionsQuery);
  const availablePlans = sortBy(
    data?.adminPlanDefinitions || [],
    'monthlyRate'
  );
  const trialDefinitionId = availablePlans.find((p) => p.tier === 'trial')?.id;

  const selectPlanOptions = availablePlans.map((plan) => [
    plan.id,
    `${plan.planName} → ${
      plan.billingInterval === 'annual'
        ? `${moneyPrintRound((plan.monthlyRate * 12) / 100)}/yr`
        : `${moneyPrintRound(plan.monthlyRate / 100) || '$0'}/mo`
    } `,
    false,
  ]);

  useEffect(() => {
    if (open) {
      setName(venue?.name || '');
      setSelectedPlanDefinitionId(
        venue?.subscription?.planDefinitionId || trialDefinitionId
      );
      setAchEnabled(venue?.achEnabled || false);
    }
  }, [open, venue, trialDefinitionId]);

  const [createVenue, { loading: createLoading }] = useMutation(
    createVenueMutation(),
    {
      onNoErrorsCompleted: () => {
        enqueueSnackbar('Created Venue', { variant: 'success' });
        onClose();
        if (afterConfirm) {
          afterConfirm();
        }
      },
    }
  );

  const loading = createLoading || plansLoading;

  const onSave = () => {
    if (!name) {
      setForceValidate(true);
      return;
    }

    const input = {
      name,
      numberOfTrialDays,
      planDefinitionId: selectedPlanDefinitionId,
      achEnabled,
      organizationId: organization.id,
    };
    const variables = { input };

    createVenue({ variables });
  };

  const afterConfirm = () => {
    onClose();
    if (refetchVenues) {
      refetchVenues();
    }
  };

  const title = `New Venue`;
  return (
    <SidebarDrawer
      open={open}
      onClose={onClose}
      title={title}
      onSave={onSave}
      loading={loading}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Fields.PvTextField
            required
            label="Venue Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            name="name"
            forceValidate={forceValidate}
          />
        </Grid>
        <Grid item xs={12}>
          <Fields.PvDropDown
            required
            label="Plan"
            name="plan"
            value={selectedPlanDefinitionId}
            onChange={(e) => setSelectedPlanDefinitionId(e.target.value)}
            vals={selectPlanOptions}
          />
        </Grid>
        <Grid item xs={12}>
          {trialDefinitionId === selectedPlanDefinitionId && (
            <Fields.PvIntegerField
              name="trialDays"
              allowNegative={false}
              value={numberOfTrialDays}
              onChange={(e) => setNumberOfTrialDays(e.target.value)}
              label="Number of trial days (starts today)"
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                className="achEnabled"
                name="achEnabled"
                checked={achEnabled}
                onChange={(e) => setAchEnabled(e.target.checked)}
              />
            }
            label="ACH Enabled"
          />
        </Grid>
      </Grid>
    </SidebarDrawer>
  );
};

export default VenueDrawer;
